import type { IAdTagCreative } from '@feathr/blackbox';
import { CreativeClass, ValidDimensions } from '@feathr/blackbox';

import { DEFAULT_AD_TAG_HTML } from '../Creatives.utils';

interface IAdTagDimensions {
  /** Dimension spec name (e.g., 'leaderboard'). */
  spec: string;
  /** Width in pixels. */
  width: number;
  /** Height in pixels. */
  height: number;
}

/**
 * Builds attributes for a new AdTagCreative with the given dimensions.
 *
 * @param campaignId - The parent campaign ID.
 * @param accountId - The account ID.
 * @param dimensions - The selected ad dimensions (spec, width, height).
 * @returns Partial attributes for creating an AdTagCreative.
 */
export function getAdTagCreativeAttributes(
  campaignId: string,
  accountId: string,
  dimensions: IAdTagDimensions,
): Partial<IAdTagCreative> {
  return {
    _cls: CreativeClass.DisplayAdTag,
    adtag: DEFAULT_AD_TAG_HTML,
    mimetype: 'text/html',
    width: dimensions.width,
    height: dimensions.height,
    spec: dimensions.spec,
    parent: campaignId,
    account: accountId,
    is_enabled: true,
    name: '',
  };
}

/**
 * Finds a dimension definition by its spec name.
 * ValidDimensions is keyed by size string (e.g., '728x90'), so we
 * need to search entries to find one matching a given spec (e.g., 'leaderboard').
 *
 * @param spec - The spec name to look up (e.g., 'leaderboard').
 * @returns The width and height, or undefined if not found.
 */
export function findDimensionBySpec(spec: string): { width: number; height: number } | undefined {
  const entry = Object.values(ValidDimensions).find((dim) => dim.spec === spec);
  if (!entry) {
    return undefined;
  }
  return { width: entry.width, height: entry.height };
}
